// @ts-check
// Regras puras da tela Configurações > Agendamentos (contrato 10 de agendamento, seção 14): rascunho do editor a partir
// da página e de volta, trechos para o site da escola, motivo de anfitrião que não pode receber e o resumo da página.

/**
 * @typedef {import('../data/booking-pages.js').BookingPage} BookingPage
 * @typedef {import('../data/booking-pages.js').BookingHost} BookingHost
 * @typedef {import('../../../shared/booking/booking-values.js').BookingPageValues} BookingPageValues
 */

// a semana do editor começa na segunda, como a agenda da escola
export const EDITOR_WEEKDAYS = Object.freeze(/** @type {const} */ (['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']));

// campos que o editor grava (o resto da página é só leitura: id, links, datas)
const EDITABLE_KEYS = Object.freeze(/** @type {const} */ ([
  'name', 'eventType', 'durationMinutes', 'bufferMinutes', 'minimumNoticeMinutes', 'maximumDaysAhead', 'slotIntervalMinutes', 'timeZone', 'locationKind',
  'location', 'availabilitySource', 'weeklyHours', 'blockedDates', 'hosts', 'hostAssignment', 'fixedGuestEmails', 'questions', 'offerMarketingOptIn',
  'confirmationMessage', 'cardStage', 'leadSource', 'allowedOrigins', 'isActive',
]));

/**
 * Rascunho do editor: cópia profunda só dos campos editáveis.
 * @param {BookingPage} page @returns {BookingPageValues}
 */
export function draftFromPage(page) {
  /** @type {Record<string, unknown>} */
  const draft = {};
  for (const key of EDITABLE_KEYS) draft[key] = structuredClone(/** @type {any} */ (page)[key]);
  draft.hosts = (page.hosts ?? []).map((host) => ({
    workspaceMemberId: host.workspaceMemberId, connectedAccountId: host.connectedAccountId ?? null,
    availabilityCalendarId: host.availabilityCalendarId ?? null, busyCalendarIds: [...(host.busyCalendarIds ?? [])],
  }));
  return /** @type {BookingPageValues} */ (draft);
}

/**
 * O que mudou entre a página gravada e o rascunho (PATCH só com isso).
 * @param {BookingPage} page @param {BookingPageValues} draft
 */
export function patchFromDraft(page, draft) {
  const saved = draftFromPage(page);
  /** @type {Record<string, unknown>} */
  const patch = {};
  for (const key of EDITABLE_KEYS) {
    if (JSON.stringify(/** @type {any} */ (saved)[key]) !== JSON.stringify(/** @type {any} */ (draft)[key])) patch[key] = /** @type {any} */ (draft)[key];
  }
  return patch;
}

/**
 * Texto livre de datas bloqueadas (uma por linha ou separadas por vírgula, AAAA-MM-DD ou DD/MM/AAAA) em AAAA-MM-DD.
 * @param {string} text @returns {{ dates: string[], invalid: string[] }}
 */
export function parseBlockedDatesText(text) {
  const dates = [];
  const invalid = [];
  for (const raw of String(text ?? '').split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean)) {
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    const brazilian = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
    const value = iso ? raw : brazilian ? `${brazilian[3]}-${brazilian[2]}-${brazilian[1]}` : null;
    if (value && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))) dates.push(value);
    else invalid.push(raw);
  }
  return { dates: [...new Set(dates)].sort(), invalid };
}

/** @param {ReadonlyArray<string>} dates */
export const blockedDatesText = (dates) => (dates ?? []).map((date) => `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`).join('\n');

/**
 * Por que o membro não pode receber agora (null quando pode): sem conta Google, conta antiga sem permissão de criar
 * eventos, conta com a autorização vencida, só Microsoft ou o membro ainda não aceitou ser anfitrião pela conta.
 * @param {BookingHost | undefined} host @param {string | null} [connectedAccountId]
 * @returns {'NO_ACCOUNT' | 'RECONNECT_REQUIRED' | 'AUTH_FAILED' | 'MICROSOFT_NOT_SUPPORTED' | 'HOST_CONSENT_REQUIRED' | null}
 */
export function hostIssue(host, connectedAccountId = null) {
  const accounts = host?.accounts ?? [];
  if (accounts.length === 0) return 'NO_ACCOUNT';
  const chosen = connectedAccountId ? accounts.find((account) => account.connectedAccountId === connectedAccountId) : accounts.find((account) => account.isEligible);
  if (chosen?.isEligible) return null;
  const google = chosen ?? accounts.find((account) => account.provider === 'GOOGLE');
  if (!google || google.provider !== 'GOOGLE') return 'MICROSOFT_NOT_SUPPORTED';
  if (google.reason === 'HOST_CONSENT_REQUIRED') return 'HOST_CONSENT_REQUIRED';
  return google.authFailed ? 'AUTH_FAILED' : 'RECONNECT_REQUIRED';
}

/**
 * O que resolve a pendência de um anfitrião (3.5.0): para si mesmo, aceitar e permitir criar eventos (ou conectar o
 * Google já como anfitrião); para outro membro, o convite por aviso e e-mail. Conta com a autorização caída precisa
 * ser reconectada em Contas conectadas pelo dono (o convite lembra isso); quem só tem Microsoft recebe o convite
 * para conectar o Google. Sem pendência, nada.
 * @param {ReturnType<typeof hostIssue>} issue @param {boolean} isSelf
 * @returns {'ACCEPT' | 'CONNECT' | 'RECONNECT' | 'INVITE' | null}
 */
export function hostAction(issue, isSelf) {
  if (issue === null) return null;
  if (!isSelf) return 'INVITE';
  if (issue === 'NO_ACCOUNT' || issue === 'MICROSOFT_NOT_SUPPORTED') return 'CONNECT';
  if (issue === 'AUTH_FAILED') return 'RECONNECT';
  return 'ACCEPT';
}

/**
 * Conta Google do anfitrião que o aceite ou a permissão de criar eventos vai usar: a escolhida na página ou a primeira.
 * @param {BookingHost | undefined} host @param {string | null} [connectedAccountId]
 */
export function hostGoogleAccount(host, connectedAccountId = null) {
  const accounts = (host?.accounts ?? []).filter((account) => account.provider === 'GOOGLE');
  return (connectedAccountId ? accounts.find((account) => account.connectedAccountId === connectedAccountId) : null) ?? accounts[0] ?? null;
}

/**
 * Situação da conta na seção "Ser anfitrião": sem o aceite; aceita mas sem o escopo `calendar.events` (a tela pede ao
 * Google pela autorização incremental); ou pronta. A conta só recebe reservas com o aceite e o escopo.
 * @param {{ acceptedAt: string | null, canCreateEvents?: boolean }} account
 * @returns {'NOT_ACCEPTED' | 'EVENT_SCOPE_REQUIRED' | 'READY'}
 */
export function hostConsentState(account) {
  if (!account.acceptedAt) return 'NOT_ACCEPTED';
  return account.canCreateEvents ? 'READY' : 'EVENT_SCOPE_REQUIRED';
}

/**
 * Trechos para colocar a página no site: link, iframe e as chamadas da API.
 * @param {{ publicUrl: string, apiBaseUrl: string, embedSnippet: string }} page
 */
export function siteSnippets(page) {
  const api = [
    `GET ${page.apiBaseUrl}`,
    `GET ${page.apiBaseUrl}/slots?from=AAAA-MM-DD&to=AAAA-MM-DD`,
    `POST ${page.apiBaseUrl}`,
    'Content-Type: application/json',
    '',
    // formToken vem do GET da página; turnstileToken, do desafio com a turnstileSiteKey do GET, quando ela vem preenchida
    JSON.stringify({
      startsAt: '2026-10-20T13:00:00.000Z', name: 'Nome completo', email: 'email@exemplo.com', phone: '11999990000', answers: {},
      consent: { accepted: true, marketingOptIn: false }, formToken: '<formToken do GET>', turnstileToken: '<token do Turnstile>',
    }, null, 2),
  ].join('\n');
  return { link: page.publicUrl, iframe: page.embedSnippet, api };
}

/**
 * Quantos dias da semana têm horário no horário semanal.
 * @param {Record<string, Array<{ start: string, end: string }>>} weeklyHours
 */
export const openWeekdayCount = (weeklyHours) => Object.values(weeklyHours ?? {}).filter((ranges) => ranges.length > 0).length;

/**
 * Próxima faixa sugerida ao acrescentar uma no dia: uma hora depois da última, sem passar de 23:00.
 * @param {ReadonlyArray<{ start: string, end: string }>} ranges
 */
export function nextRange(ranges) {
  const last = ranges[ranges.length - 1];
  if (!last) return { start: '09:00', end: '12:00' };
  const [hours] = last.end.split(':').map(Number);
  const start = Math.min(hours + 1, 22);
  return { start: `${String(start).padStart(2, '0')}:00`, end: `${String(Math.min(start + 2, 23)).padStart(2, '0')}:00` };
}
